import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FAQ, { getFAQSchema } from '@/pages/home/components/FAQ';
import { useEffect } from 'react';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://biffdigital.com';
const pagePath = '/sektorler/endustriyel-b2b-pazarlama';
const pageUrl = `${siteUrl}${pagePath}`;

const b2bFAQItems = [
  {
    question: 'B2B pazarlama ile B2C pazarlama arasındaki fark nedir?',
    answer:
      'B2B\'de satın alma kararı daha uzun, daha rasyonel ve daha çok kişinin dahil olduğu bir süreçtir. Kimse "sepete ekle" butonuna basmaz. Bu nedenle B2B pazarlama, güven inşası, teknik iletişim ve uzun satış döngüsü yönetimi gerektirir.',
  },
  {
    question: 'Endüstriyel firmalar için dijital pazarlama işe yarar mı?',
    answer:
      'Kesinlikle. Endüstriyel satın alma süreçlerinin %67\'si dijital araştırmayla başlar. Karar vericiler satın alma yapmadan önce Google\'da araştırır, LinkedIn\'de inceler ve web sitenizi ziyaret eder. Dijitalde görünür ve güvenilir olmazsanız, daha ilk turda elenirsiniz.',
  },
  {
    question: 'B2B lead generation nasıl yapılır?',
    answer:
      'LinkedIn, sektörel SEO, içerik pazarlaması ve hedefli reklam kampanyalarıyla ideal müşteri profiline (ICP) uygun karar vericilere ulaşıyoruz. Lead\'leri sadece toplamakla kalmıyor, satış ekibinizin kullanabileceği şekilde nitelendiriyoruz.',
  },
  {
    question: 'Teknik ürünlerin pazarlaması nasıl yapılır?',
    answer:
      'Teknik ürünü insanların anlayacağı hikayeye çeviriyoruz. Mühendislerin yazdığı teknik dokümanları, karar vericilerin anlayacağı değer önerilerine dönüştürüyoruz. Teknik doğruluktan ödün vermeden, ticari etkiyi öne çıkarıyoruz.',
  },
  {
    question: 'B2B pazarlamada en etkili kanal hangisi?',
    answer:
      'Tek bir kanal yoktur. LinkedIn kurumsal erişim için, SEO organik keşif için, e-posta doğrudan iletişim için, sektörel yayınlar otorite için kullanılır. BIFF olarak tüm kanalları satın alma yolculuğunun farklı aşamalarında konumlandırıyoruz.',
  },
  {
    question: 'B2B pazarlamada sonuçlar ne zaman görülür?',
    answer:
      'SEO ve içerik pazarlaması 3-6 ayda ivme kazanır. LinkedIn reklamları ve lead generation kampanyaları ilk 30 günde sonuç vermeye başlar. Ancak asıl başarı, bu kısa ve uzun vadeli stratejilerin birlikte yürütülmesindedir.',
  },
];

const faqSchema = getFAQSchema(b2bFAQItems);

const pageSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      name: 'Endüstriyel & B2B Pazarlama Hizmeti',
      provider: {
        '@type': 'Organization',
        name: 'BIFF Digital',
        url: siteUrl,
        telephone: '+905557101968',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Söğütözü Cad. No: 2 Koç Kuleleri C Blok 17',
          addressLocality: 'Çankaya',
          addressRegion: 'Ankara',
          addressCountry: 'TR',
          postalCode: '06530',
        },
      },
      description:
        'Endüstriyel ve B2B firmalar için lead generation, LinkedIn, SEO ve içerik pazarlamasıyla uzun satış döngülerini yönetiyoruz. BIFF Digital B2B pazarlamayı strateji ve teknik iletişimle birlikte yürütür.',
      url: pageUrl,
      areaServed: { '@type': 'Country', name: 'Turkey' },
      serviceType: 'Endüstriyel & B2B Pazarlama',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'B2B Pazarlama Hizmet Detayları',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'B2B Strateji', description: 'Uzun satış döngüsüne uygun pazarlama stratejisi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Lead Generation', description: 'Nitelikli B2B lead üretimi ve yönetimi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'LinkedIn Pazarlama', description: 'LinkedIn reklamları ve organik büyüme stratejisi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Teknik İletişim', description: 'Karmaşık ürünleri anlaşılır değer önerilerine dönüştürme' } },
        ],
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Sektörler', item: `${siteUrl}/sektorler/endustriyel-b2b-pazarlama` },
        { '@type': 'ListItem', position: 3, name: 'Endüstriyel & B2B', item: pageUrl },
      ],
    },
    {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.speakable-summary'],
    },
    faqSchema,
  ],
};

export default function EndustriyelB2B() {
  useEffect(() => {
    document.title = 'Endüstriyel & B2B Pazarlama Ajansı | Lead Generation | BIFF Digital';

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', pageUrl);

    return () => {
      link?.setAttribute('href', siteUrl);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">

        {/* HERO */}
        <section className="relative w-full overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Cinematic%20industrial%20factory%20interior%20with%20large%20machinery%20and%20steel%20structures%20in%20dramatic%20dark%20atmosphere%2C%20crimson%20red%20accent%20lighting%2C%20B2B%20manufacturing%20concept%2C%20dark%20charcoal%20and%20red%20tones%2C%20premium%20editorial%20photography%2C%20dramatic%20lighting%2C%20no%20people%2C%20wide%20angle&width=1600&height=900&seq=b2b-hero-01&orientation=landscape"
              alt="Endüstriyel ve B2B Pazarlama - Lead Generation Stratejisi"
              title="Endüstriyel & B2B Pazarlama - BIFF Digital"
              className="w-full h-[420px] md:h-[560px] object-cover object-top"
              fetchPriority="high"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30" />
          <div className="relative z-10 w-full h-[420px] md:h-[560px] flex items-center justify-center">
            <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-4">SEKTÖRLER</p>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white tracking-tighter leading-none mb-6">
                Endüstriyel &amp;<br />B2B Pazarlama
              </h1>
              <p className="text-xl md:text-2xl text-white font-medium leading-relaxed mb-6">
                B2B&apos;de Kimse<br />
                <span className="text-primary-500">&ldquo;Sepete Ekle&rdquo; Butonuna Basmaz.</span>
              </p>
              <p className="text-sm md:text-base text-white/80 leading-relaxed max-w-2xl mx-auto speakable-summary">
                Uzun satış döngülerine kısa pazarlama çözümleri uygulanmaz. B2B pazarlama; güven, otorite ve sabır ister. Biz endüstriyel ve B2B firmaların satın alma yolculuğunu veriyle yönetiyor, teknik ürünü insanların anlayacağı hikayeye çeviriyoruz.
              </p>
            </div>
          </div>
        </section>

        {/* Uzun Satış Döngüleri */}
        <section className="section-padding">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-8">
              Uzun Satış Döngülerine<br />Kısa Pazarlama Çözümleri Uygulanmaz.
            </h2>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-6">
              Bir endüstriyel ürünün satışı 3 ay, 6 ay, bazen 12 ay sürebilir. Bu süre boyunca karar vericiler araştırır, karşılaştırır, referans ister, teknik doküman inceler, fiyat teklifi alır. B2B pazarlama, bu uzun yolculuğun her adımında markanızı doğru konumlandırmaktır.
            </p>
            <div className="glass-card rounded-2xl p-6 md:p-8 border-l-4 border-primary-500 mb-8">
              <p className="text-base md:text-lg text-foreground-50 font-bold leading-relaxed">
                Pazarlama ve satış aynı masada olmalı.
              </p>
              <p className="text-sm text-foreground-300 mt-3 leading-relaxed">
                B2B&apos;de pazarlama ve satış birbirinden ayrı düşünülemez. Biz pazarlama stratejisini satış ekibinin ihtiyaçlarıyla birlikte kurguluyor, lead&apos;leri satışa hazır hale getiriyoruz.
              </p>
            </div>
          </div>
        </section>

        {/* Ne Yapıyoruz */}
        <section className="section-padding bg-background-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 md:mb-14">
              <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-2">NE YAPIYORUZ?</p>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter">
                Uzun Döngüye<br />Bütüncül Strateji
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-5 md:gap-6">
              {[
                { icon: 'ri-route-line', title: 'B2B Strateji', desc: 'Satış döngüsü, karar verici profilleri ve satın alma sürecini analiz ederek pazarlama stratejisini kurguluyoruz. Her aşama için ayrı mesaj, ayrı kanal, ayrı içerik.' },
                { icon: 'ri-user-search-line', title: 'Lead Generation', desc: 'İdeal müşteri profiline uygun, satın alma niyeti olan karar vericilere ulaşıyoruz. Lead\'leri skorluyor, nitelendiriyor ve satış ekibinizin kullanımına hazır hale getiriyoruz.' },
                { icon: 'ri-linkedin-line', title: 'LinkedIn Pazarlama', desc: 'LinkedIn reklamları, InMail kampanyaları ve organik içerik stratejisiyle B2B karar vericilere doğrudan ulaşıyoruz. Doğru şirkette, doğru unvandaki kişiye, doğru mesajla.' },
                { icon: 'ri-search-line', title: 'SEO & İçerik', desc: 'Endüstriyel arama terimlerinde organik görünürlük ve sektörel otorite inşa ediyoruz. Teknik blog yazıları, whitepaper\'lar ve vaka çalışmalarıyla karar vericilerin araştırma sürecinde yanlarında oluyoruz.' },
                { icon: 'ri-file-code-line', title: 'Teknik İletişim', desc: 'Mühendislik dilini iş diline çeviriyoruz. Karmaşık teknik özellikleri, karar vericinin anlayacağı ve değer göreceği şekilde anlatıyoruz. Teknik doğruluktan ödün vermeden, ticari etkiyi öne çıkarıyoruz.' },
                { icon: 'ri-bar-chart-box-line', title: 'CRM & Satış Hunisi', desc: 'Pazarlama ve satış arasında kesintisiz veri akışı sağlıyoruz. Hangi lead nereden geldi, hangi aşamada, neden ilerlemiyor — tüm bu soruların cevabını CRM üzerinden canlı takip ediyoruz.' },
              ].map((item, i) => (
                <div key={i} className="glass-card-hover rounded-2xl p-6 md:p-8 group">
                  <div className="w-12 h-12 bg-background-200 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary-500/10 transition-colors duration-300">
                    <i className={`${item.icon} text-xl text-primary-500`} />
                  </div>
                  <h3 className="text-lg font-bold text-foreground-50 mb-3">{item.title}</h3>
                  <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Teknik Ürünü Hikayeye Çevirmek */}
        <section className="section-padding">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="glass-card rounded-2xl p-6 md:p-8 border border-white/5">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-50 tracking-tighter mb-4">
                Teknik Ürünü İnsanların Anlayacağı<br />Hikayeye Çeviriyoruz.
              </h2>
              <p className="text-sm text-foreground-300 leading-relaxed">
                Endüstriyel firmaların en büyük pazarlama sorunu, ürünlerini mühendisler gibi anlatmalarıdır. Teknik özellikler önemlidir, ama karar verici &ldquo;Bu ürün benim hangi sorunumu çözecek?&rdquo; sorusunun cevabını arar. Biz bu cevabı net, ikna edici ve ticari bir dille veriyoruz.
              </p>
            </div>
          </div>
        </section>

        {/* Süreç */}
        <section className="section-padding bg-background-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-10">
              B2B Pazarlama Sürecimiz
            </h2>
            <div className="space-y-6">
              {[
                { step: '01', title: 'ICP & Pazar Analizi', desc: 'İdeal müşteri profilini tanımlıyor, sektör dinamiklerini ve rakip konumlandırmasını analiz ediyoruz. Kime, ne söyleyeceğimizi bilmeden başlamıyoruz.' },
                { step: '02', title: 'İçerik ve Kanal Stratejisi', desc: 'Satın alma yolculuğunun her aşaması için içerik ve kanal planı oluşturuyoruz. Farkındalık aşamasında SEO ve LinkedIn, değerlendirme aşamasında vaka çalışmaları ve whitepaper, karar aşamasında demo ve teklif.' },
                { step: '03', title: 'Lead Generation ve Kampanyalar', desc: 'Hedefli reklam kampanyaları, LinkedIn outreach ve içerik pazarlamasıyla nitelikli lead akışı başlatıyoruz.' },
                { step: '04', title: 'Lead Nurturing ve Satış Desteği', desc: 'Lead\'leri skorluyor, e-posta otomasyonlarıyla besliyor ve satış ekibine sıcak lead olarak aktarıyoruz. CRM entegrasyonuyla sürecin tamamını ölçüyoruz.' },
              ].map((item, i) => (
                <div key={i} className="glass-card rounded-2xl p-5 md:p-6 flex items-start gap-5">
                  <span className="text-2xl font-black text-primary-500 font-heading shrink-0">{item.step}</span>
                  <div>
                    <h3 className="text-base font-bold text-foreground-100 mb-1">{item.title}</h3>
                    <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQ
          items={b2bFAQItems}
          sectionTitle="SIK SORULANLAR"
          sectionSubtitle="ENDÜSTRİYEL & B2B"
          sectionSummary="Endüstriyel ve B2B pazarlama hakkında en sık sorulan soruları, doğrudan ve net cevaplarla burada topladık."
          speakableClass={false}
        />

        {/* CTA */}
        <section className="section-padding bg-background-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="glass-card rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/5 rounded-bl-full -mr-16 -mt-16" />
              <div className="relative z-10">
                <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-50 tracking-tighter mb-4">
                  Daha fazla lead değil.<br />
                  <span className="text-primary-500">Daha doğru lead.</span>
                </h2>
                <p className="text-sm md:text-base text-foreground-400 mb-8 max-w-xl mx-auto leading-relaxed">
                  Mevcut lead generation sürecinizi analiz edelim. Hangi kanaldan ne kadar lead geliyor, ne kadarı satışa dönüşüyor — veriyle görelim.
                </p>
                <a href="/iletisim" className="btn-primary text-base px-8 py-4">
                  Ücretsiz B2B Analizi
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}